import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Card,
  CardContent,
  DataTable,
  EmptyState,
  FullscreenSpinner,
  Input,
  type Column,
} from '@shared/ui';
import {
  buyRequestApi,
  type BuyRequestListItem,
  type BuyRequestStatus,
} from '@entities/buy-request';
import { formatDateTime } from '@shared/lib/format';
import { ROUTES } from '@shared/config/routes';

const COLUMNS: Column<BuyRequestListItem>[] = [
  {
    key: 'name',
    header: 'Заявка',
    cell: (r) => (
      <Link to={ROUTES.buyRequestDetail(r.id)} className="font-medium text-primary hover:underline">
        {r.name}
      </Link>
    ),
  },
  { key: 'dest', header: 'Куда', cell: (r) => r.destinationCountry },
  { key: 'vol', header: 'Объём', cell: (r) => r.volume ?? '—' },
  {
    key: 'budget',
    header: 'Бюджет',
    cell: (r) => (r.budget != null ? `${r.budget} ${r.currency ?? ''}` : '—'),
  },
  { key: 'dispatch', header: 'Dispatch', cell: (r) => <Badge variant="outline">{r.dispatchMode}</Badge> },
  { key: 'status', header: 'Статус', cell: (r) => <Badge variant="outline">{r.status}</Badge> },
  {
    key: 'hidden',
    header: '',
    cell: (r) => (r.hiddenByAdmin ? <Badge variant="destructive">hidden</Badge> : null),
  },
  { key: 'q', header: 'Котировок', cell: (r) => r.quoteCount },
  { key: 'at', header: 'Создана', cell: (r) => formatDateTime(r.createdAt) },
];

export function BuyRequestsListPage() {
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') as BuyRequestStatus | null) ?? undefined;
  const hidden = (params.get('hidden') as 'true' | 'false' | 'all' | null) ?? undefined;
  const q = params.get('q') ?? '';

  const { data, isLoading } = useQuery({
    queryKey: ['buy-requests', { status, hidden, q }],
    queryFn: () => buyRequestApi.list({ status, hidden, q: q || undefined, size: 50 }),
  });

  const update = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value == null || value === '') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  if (isLoading) return <FullscreenSpinner />;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Buy Requests</h1>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Поиск по названию"
          defaultValue={q}
          onBlur={(e) => update('q', e.target.value)}
          className="max-w-sm"
        />
        <select
          value={status ?? ''}
          onChange={(e) => update('status', e.target.value || null)}
          className="h-10 rounded-md border bg-background px-2 text-sm"
        >
          <option value="">Все статусы</option>
          <option value="OPEN">OPEN</option>
          <option value="CLOSED">CLOSED</option>
          <option value="EXPIRED">EXPIRED</option>
          <option value="CANCELLED">CANCELLED</option>
        </select>
        <select
          value={hidden ?? ''}
          onChange={(e) => update('hidden', e.target.value || null)}
          className="h-10 rounded-md border bg-background px-2 text-sm"
        >
          <option value="">Любые</option>
          <option value="true">Скрытые</option>
          <option value="false">Видимые</option>
        </select>
      </div>
      <Card>
        <CardContent className="p-0">
          <DataTable
            rows={data?.content ?? []}
            columns={COLUMNS}
            rowKey={(r) => r.id}
            empty={<EmptyState title="Заявок не найдено" />}
          />
        </CardContent>
      </Card>
    </div>
  );
}
